import React, { useState } from 'react';
import { X, DollarSign, Calendar, User, Phone, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

const PaymentModal = ({ booking, onClose, onConfirm }) => {
  // Helper function to calculate total amount correctly (baseAmount - discountAmount)
  const calculateTotalAmount = (booking) => {
    // If baseAmount and discountAmount are available, calculate from them
    if (booking.baseAmount !== undefined) {
      const base = parseFloat(booking.baseAmount || 0);
      const discount = parseFloat(booking.discountAmount || 0);
      return Math.max(0, base - discount);
    }
    // Otherwise use the provided totalAmount
    return parseFloat(booking.totalAmount || booking.price || 0);
  };

  // Calculate amounts correctly
  const totalAmount = calculateTotalAmount(booking);
  const currentAdvance = parseFloat(booking.advanceAmount || 0);
  // Balance is always calculated as: Total Amount - Advance Amount
  const currentBalance = Math.max(0, totalAmount - currentAdvance);

  const [paymentData, setPaymentData] = useState({
    amountPaid: currentBalance,
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    paymentMode: booking.paymentDetails?.paymentMode || 'cash', // Default to cash
    paidBy: booking.contactName || booking.bookedBy || '',
    mobile: booking.contactPhone || booking.bookedByPhone || ''
  });

  const [errors, setErrors] = useState({});

  // Recalculate when amountPaid changes
  const amountPaid = parseFloat(paymentData.amountPaid) || 0;
  const newBalance = Math.max(0, currentBalance - amountPaid);
  const newAdvance = currentAdvance + amountPaid;

  const validateForm = () => {
    const newErrors = {};

    if (!paymentData.amountPaid || parseFloat(paymentData.amountPaid) <= 0) {
      newErrors.amountPaid = 'Amount paid must be greater than 0';
    } else if (parseFloat(paymentData.amountPaid) > currentBalance) {
      newErrors.amountPaid = `Amount cannot exceed balance of ₹${currentBalance}`;
    }

    if (!paymentData.paymentDate) {
      newErrors.paymentDate = 'Payment date is required';
    }

    if (!paymentData.paidBy || !paymentData.paidBy.trim()) {
      newErrors.paidBy = 'Paid by name is required';
    }

    if (!paymentData.mobile || !paymentData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else {
      const mobileDigits = paymentData.mobile.replace(/\D/g, '');
      if (mobileDigits.length !== 10) {
        newErrors.mobile = 'Please enter a valid 10-digit mobile number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const paymentInfo = {
      ...paymentData,
      amountPaid: parseFloat(paymentData.amountPaid),
      newAdvance,
      newBalance,
      isFullyPaid: newBalance <= 0
    };

    onConfirm(paymentInfo);
  };

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-ga-bg-surface text-ga-text-primary rounded-ga-md shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-ga-primary p-6 rounded-t-ga-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-ga-bg-surface rounded-full p-2">
                <DollarSign className="h-6 w-6 text-ga-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-ga-text-on-primary">Record Payment</h2>
                <p className="text-ga-text-on-primary/80 text-sm">Enter payment details</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-ga-text-on-primary hover:opacity-80 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Booking Summary */}
        <div className="p-6 border-b border-ga-border-subtle bg-ga-bg-surface-subtle">
          <h3 className="font-semibold text-ga-text-primary mb-3">Booking Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-ga-text-muted">Service:</span>
              <span className="font-medium">{booking.serviceName || booking.groundName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ga-text-muted">Total Amount:</span>
              <span className="font-medium">₹{totalAmount.toFixed(2)}</span>
            </div>
            {booking.baseAmount !== undefined && Math.abs(parseFloat(booking.baseAmount || 0) - totalAmount) > 0.01 && (
              <div className="flex justify-between text-xs text-ga-text-muted">
                <span>Base: ₹{parseFloat(booking.baseAmount || 0).toFixed(2)}</span>
                {booking.discountAmount > 0 && (
                  <span className="text-ga-success">
                    Discount: -₹{parseFloat(booking.discountAmount || 0).toFixed(2)}
                  </span>
                )}
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-ga-text-muted">Already Paid:</span>
              <span className="font-medium text-ga-success">₹{currentAdvance.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-ga-border-subtle pt-2">
              <span className="text-ga-text-primary font-semibold">Balance Due:</span>
              <span className="font-bold text-ga-warning">₹{currentBalance.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Amount Paid */}
          <div>
            <label className="block text-sm font-medium text-ga-text-muted mb-2">
              <DollarSign className="h-4 w-4 inline mr-1" />
              Amount Paid (₹) *
            </label>
            <input
              type="number"
              min="0"
              max={currentBalance}
              step="0.01"
              value={paymentData.amountPaid}
              onChange={(e) => handleInputChange('amountPaid', e.target.value)}
              className="ga-input w-full rounded-ga-sm"
              placeholder="Enter amount paid"
              required
            />
            {errors.amountPaid && (
              <p className="text-ga-danger text-sm mt-1">{errors.amountPaid}</p>
            )}
            <p className="text-xs text-ga-text-muted mt-1">
              Maximum: ₹{currentBalance.toFixed(2)}
            </p>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-ga-text-muted mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Payment Date *
            </label>
            <input
              type="date"
              value={paymentData.paymentDate}
              onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              max={format(new Date(), 'yyyy-MM-dd')}
              className="ga-input w-full rounded-ga-sm"
              required
            />
            {errors.paymentDate && (
              <p className="text-ga-danger text-sm mt-1">{errors.paymentDate}</p>
            )}
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-sm font-medium text-ga-text-muted mb-2">
              <DollarSign className="h-4 w-4 inline mr-1" />
              Payment Mode *
            </label>
            <select
              value={paymentData.paymentMode}
              onChange={(e) => handleInputChange('paymentMode', e.target.value)}
              className="ga-input w-full rounded-ga-sm"
              required
            >
              <option value="cash">Cash</option>
              <option value="card">Card (Credit/Debit)</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="online">Online Payment</option>
              <option value="other">Other</option>
            </select>
            {errors.paymentMode && (
              <p className="text-ga-danger text-sm mt-1">{errors.paymentMode}</p>
            )}
          </div>

          {/* Paid By */}
          <div>
            <label className="block text-sm font-medium text-ga-text-muted mb-2">
              <User className="h-4 w-4 inline mr-1" />
              Paid By *
            </label>
            <input
              type="text"
              value={paymentData.paidBy}
              onChange={(e) => handleInputChange('paidBy', e.target.value)}
              className="ga-input w-full rounded-ga-sm"
              placeholder="Enter name of person who paid"
              required
            />
            {errors.paidBy && (
              <p className="text-ga-danger text-sm mt-1">{errors.paidBy}</p>
            )}
          </div>

          {/* Mobile Number */}
          <div>
            <label className="block text-sm font-medium text-ga-text-muted mb-2">
              <Phone className="h-4 w-4 inline mr-1" />
              Mobile Number *
            </label>
            <input
              type="tel"
              value={paymentData.mobile}
              onChange={(e) => handleInputChange('mobile', e.target.value)}
              className="ga-input w-full rounded-ga-sm"
              placeholder="Enter 10-digit mobile number"
              required
            />
            {errors.mobile && (
              <p className="text-ga-danger text-sm mt-1">{errors.mobile}</p>
            )}
          </div>

          {/* Payment Summary */}
          <div className="bg-ga-primary-soft border border-ga-primary/30 rounded-ga-md p-4">
            <h4 className="font-semibold text-ga-text-primary mb-3">Payment Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-ga-text-muted">Current Balance:</span>
                <span className="font-medium">₹{currentBalance.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ga-text-muted">Amount Paying:</span>
                <span className="font-medium">₹{amountPaid.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ga-text-muted">Payment Mode:</span>
                <span className="font-medium capitalize">
                  {paymentData.paymentMode === 'bank_transfer' ? 'Bank Transfer' :
                   paymentData.paymentMode === 'upi' ? 'UPI' :
                   paymentData.paymentMode === 'card' ? 'Card' :
                   paymentData.paymentMode}
                </span>
              </div>
              <div className="border-t border-ga-border-subtle pt-2 flex justify-between font-semibold">
                <span className="text-ga-text-primary">Remaining Balance:</span>
                <span className={`${
                  newBalance > 0 ? 'text-ga-warning' : 'text-ga-success'
                }`}>
                  ₹{newBalance.toFixed(2)}
                </span>
              </div>
              {newBalance <= 0 && (
                <div className="bg-ga-success/15 border border-ga-success/30 rounded-ga-sm p-2 mt-2">
                  <p className="text-xs text-ga-success flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Payment will be marked as fully paid
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="ga-btn-ghost flex-1 px-4 py-2 rounded-ga-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ga-btn-primary flex-1 px-4 py-2 rounded-ga-md font-medium"
            >
              Record Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;

